import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { api } from '../api'
import Anliegenliste from '../komponenten/Anliegenliste'
import AssistentVorgaben from '../komponenten/AssistentVorgaben'
import GemerkteEntscheidungen from '../komponenten/GemerkteEntscheidungen'
import Journal from '../komponenten/Journal'
import MailEingaenge from '../komponenten/MailEingaenge'
import RechenbasisStandard from '../komponenten/RechenbasisStandard'
import SelectLineDienst from '../komponenten/SelectLineDienst'
import Meldung from '../komponenten/Meldung'
import TaktStandard from '../komponenten/TaktStandard'
import Waechterliste from '../komponenten/Waechterliste'
import Zugaenge from '../komponenten/Zugaenge'
import { zeitpunkt } from '../format'
import { useAktion, useLaden } from '../hooks'
import { useSitzung } from '../sitzung'
import type { MailAbsender, PostfachStatus } from '../typen'

/**
 * Einstellungen: das Postfach.
 *
 * Wer an othuena@intsode.com schicken darf, steht hier - nicht in einer Serverdatei. Eine Mail von
 * jemand anderem wird festgehalten, aber nicht ausgewertet; sie taucht unten in der Liste auf und
 * lässt sich mit einem Klick freischalten und nachholen. Aus einer erlaubten Mail wird je nach
 * Inhalt ein PDF-Import, ein Excel-Import mit Plan oder eine Freitext-Erfassung - immer als
 * Vorschlag unter Importe, nie direkt in der Kladde.
 *
 * Darunter (18.09.2026) die gemerkten Entscheidungen des Excel-Imports und das Journal der
 * schreibenden Aktionen - beides eigene Komponenten, diese Datei bleibt das Postfach.
 *
 * Seit dem 06.10.2026 zwei Postfächer und zwei Bereiche: jede Mail wird in Verpackung oder
 * Rohstoffnotierungen sortiert (MailEingaenge.tsx zeigt Bereich und Grund und ordnet Unklares per
 * Knopf zu), und die Verwaltung gibt unter „Zugänge" die Bereiche je Benutzer frei.
 *
 * Seit dem 02.11.2026 steht hier auch, was der Assistent sich merkt: Vorgaben für künftige Gespräche,
 * Wächter („sag mir, wenn …") und Anliegen an den Entwickler - jeweils einsehbar und änderbar, damit
 * nichts davon nur im Gespräch versprochen ist.
 */

export default function EinstellungenSeite() {
  const [status, setStatus] = useState<PostfachStatus | null>(null)
  const [absender, setAbsender] = useState<MailAbsender[]>([])
  // Nach „Jetzt abholen" oder „Erlauben" baut sich die Mail-Liste neu auf.
  const [mailStand, setMailStand] = useState(0)
  const { benutzer } = useSitzung()
  const verwaltung = benutzer.rolle === 'verwaltung'
  const [neueAdresse, setNeueAdresse] = useState('')
  const [neuerName, setNeuerName] = useState('')
  const [laeuft, setLaeuft] = useState(false)
  const { fehler, erfolg, setErfolg, ausfuehren } = useAktion()

  const { fehler: ladeFehler, neuLaden: laden } = useLaden(
    () => Promise.all([api.postfachStatus(), api.postfachAbsender()]),
    [],
    {
      onGeladen: ([s, a]) => {
        setStatus(s)
        setAbsender(a)
      },
    },
  )

  function anlegen(adresse: string, name: string | null) {
    void ausfuehren(async () => {
      const neu = await api.postfachAbsenderAnlegen(adresse, name)
      setErfolg(`${neu.adresse} darf jetzt schicken.`)
      setNeueAdresse('')
      setNeuerName('')
      laden()
      setMailStand((n) => n + 1)
    })
  }

  function loeschen(eintrag: MailAbsender) {
    const id = eintrag.id
    if (id === null) return
    if (!window.confirm(`${eintrag.adresse} entfernen? Mails von dort werden dann nur noch festgehalten, nicht ausgewertet.`)) return
    void ausfuehren(async () => {
      await api.postfachAbsenderLoeschen(id)
      laden()
    })
  }

  function abholen() {
    setLaeuft(true)
    void ausfuehren(async () => {
      const s = await api.postfachAbholen()
      setStatus(s)
      setErfolg(
        s.letzter_fehler
          ? `Abholung fehlgeschlagen: ${s.letzter_fehler}`
          : s.anzahl_zuletzt === 0
            ? 'Abgeholt - nichts Neues im Postfach.'
            : `Abgeholt - ${s.anzahl_zuletzt} Anhang/Text ausgewertet. Die Läufe stehen unter Importe des jeweiligen Bereichs.`,
      )
      setMailStand((n) => n + 1)
    }).finally(() => setLaeuft(false))
  }

  const postfaecher = status?.postfaecher ?? []

  // Ein Link wie „/einstellungen#waechter" (von „Heute") soll beim Abschnitt landen - der steht erst nach
  // dem ersten Rendern da, darum hier und nicht vom Browser.
  const { hash } = useLocation()
  useEffect(() => {
    if (!hash) return
    const ziel = window.setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' }), 0)
    return () => window.clearTimeout(ziel)
  }, [hash])

  return (
    <>
      <h2>Einstellungen</h2>
      <Unterreiter abschnitte={verwaltung ? [...ABSCHNITTE, { id: 'zugaenge', titel: 'Zugänge' }] : ABSCHNITTE} />
      {(ladeFehler ?? fehler) && <Meldung art="fehler">{ladeFehler ?? fehler}</Meldung>}
      {erfolg && <Meldung art="gut">{erfolg}</Meldung>}

      <h3 id="postfach">{postfaecher.length > 1 ? 'Postfächer' : 'Postfach'}</h3>
      <div className="karte">
        <div className="leiste">
          {postfaecher.length <= 1 ? (
            <>
              <b>Postfach {status?.adresse ?? ''}</b>
              {status && !status.eingerichtet && <span className="status gesperrt">auf dem Server nicht eingerichtet</span>}
              {status?.eingerichtet && (
                <span className="hinweis">
                  Abholung alle {Math.round(status.intervall_sekunden / 60)} Minuten
                  {status.letzte_abholung ? `, zuletzt ${zeitpunkt(status.letzte_abholung)}` : ', seit dem Start noch nicht'}
                  {status.letzter_fehler ? ` - Fehler: ${status.letzter_fehler}` : ''}
                </span>
              )}
            </>
          ) : (
            <>
              <b>{postfaecher.length} Postfächer</b>
              {status && <span className="hinweis">Abholung alle {Math.round(status.intervall_sekunden / 60)} Minuten</span>}
            </>
          )}
          <button style={{ marginLeft: 'auto' }} disabled={!status?.eingerichtet || laeuft} onClick={abholen}>
            {laeuft ? 'Holt ab…' : 'Jetzt abholen'}
          </button>
        </div>
        {postfaecher.length > 1 && (
          <div className="tabellenrahmen" style={{ marginBottom: 10 }}>
            <table>
              <thead>
                <tr>
                  <th>Adresse</th>
                  <th>Stand</th>
                  <th>Zuletzt abgeholt</th>
                  <th className="zahl">Zuletzt gelesen</th>
                  <th>Fehler</th>
                </tr>
              </thead>
              <tbody>
                {postfaecher.map((p) => (
                  <tr key={p.adresse}>
                    <td>{p.adresse}</td>
                    <td>
                      <span className={`status ${p.eingerichtet ? (p.letzter_fehler ? 'fehler' : 'gebucht') : 'gesperrt'}`}>
                        {p.eingerichtet ? (p.letzter_fehler ? 'Fehler' : 'holt ab') : 'nicht eingerichtet'}
                      </span>
                    </td>
                    <td>{p.letzte_abholung ? zeitpunkt(p.letzte_abholung) : <span className="hinweis">seit dem Start noch nicht</span>}</td>
                    <td className="zahl">{p.anzahl_zuletzt}</td>
                    <td>{p.letzter_fehler ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="hinweis">
          Was per Mail ankommt, wird nie direkt gebucht. Jede Mail wird zuerst einem Bereich zugeordnet - am Absender,
          an Notierungswörtern im Betreff, sonst von der KI; bleibt es unklar, entscheiden Sie unten per Knopf.
          Verpackung: ein PDF-Report wird ausgewertet, eine Excel-Datei bekommt einen Plan, reiner Text wird wie eine
          Freitext-Erfassung gelesen - alles wartet unter Importe auf Freigabe. Rohstoffnotierungen: jede Mail wird ein
          Lauf, der nur dann von selbst übernommen wird, wenn alle Prüfungen bestehen.
        </p>
        {status && !status.absender_gepflegt && (
          <Meldung art="warnung">
            Es ist kein erlaubter Absender eingetragen. Bis dahin wird jede Mail nur festgehalten und nicht ausgewertet.
          </Meldung>
        )}
      </div>

      <div className="karte">
        <div className="leiste">
          <b>Wer schicken darf</b>
          <span className="hinweis">Ganze Adresse (name@firma.de) oder eine ganze Domäne (@firma.de).</span>
        </div>
        <div className="tabellenrahmen">
          <table>
            <thead>
              <tr>
                <th>Adresse</th>
                <th>Name</th>
                <th>Seit</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {absender.length === 0 && (
                <tr>
                  <td colSpan={4} className="leer">
                    Noch niemand.
                  </td>
                </tr>
              )}
              {absender.map((a) => (
                <tr key={`${a.quelle}-${a.adresse}`}>
                  <td>{a.adresse}</td>
                  <td>{a.name ?? (a.quelle === 'konfiguration' ? <span className="hinweis">aus der Serverkonfiguration</span> : '')}</td>
                  <td>{a.angelegt_am ? zeitpunkt(a.angelegt_am) : ''}</td>
                  <td>
                    {a.id !== null && (
                      <button className="leise" onClick={() => loeschen(a)}>
                        Entfernen
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <form
          className="leiste"
          onSubmit={(e) => {
            e.preventDefault()
            if (neueAdresse.trim()) anlegen(neueAdresse, neuerName.trim() || null)
          }}
        >
          <label className="feld">
            <span className="hinweis">Adresse oder @Domäne</span>
            <input type="text" value={neueAdresse} onChange={(e) => setNeueAdresse(e.target.value)} placeholder="s.barth@othuena.de" size={28} />
          </label>
          <label className="feld">
            <span className="hinweis">Name (optional)</span>
            <input type="text" value={neuerName} onChange={(e) => setNeuerName(e.target.value)} placeholder="Frau Barth" size={20} />
          </label>
          <button className="haupt" type="submit" disabled={!neueAdresse.trim()}>
            Erlauben
          </button>
        </form>
      </div>

      <div className="karte">
        <div className="leiste">
          <b>Zuletzt angekommen</b>
          <span className="hinweis">Jede Mail wird festgehalten, auch eine abgewiesene. Absender freischalten und „Nachholen“ wertet sie aus.</span>
        </div>
        <MailEingaenge key={mailStand} onAbsenderErlaubt={laden} />
      </div>

      <h3 id="rechenbasis">Rechenbasis der Disposition</h3>
      <RechenbasisStandard />

      <h3 id="takt">Takt der Disposition</h3>
      <TaktStandard />

      <h3 id="vorgaben">Vorgaben für den Assistenten</h3>
      <div className="karte">
        <AssistentVorgaben />
      </div>

      <h3 id="waechter">Wächter</h3>
      <Waechterliste />

      <h3 id="anliegen">Anliegen an den Entwickler</h3>
      <Anliegenliste />

      <h3 id="selectline">SelectLine-Abholdienst</h3>
      <SelectLineDienst />

      <h3 id="gemerkte-entscheidungen">Gemerkte Entscheidungen</h3>
      <GemerkteEntscheidungen />

      <h3 id="journal">Journal</h3>
      <Journal />

      {verwaltung && (
        <>
          <h3 id="zugaenge">Zugänge</h3>
          <Zugaenge />
        </>
      )}
    </>
  )
}

/** Die Abschnitte dieser Seite - die Reiterzeile oben führt nur hierher, das Journal fände man sonst nicht. */
const ABSCHNITTE = [
  { id: 'postfach', titel: 'Postfach' },
  { id: 'rechenbasis', titel: 'Rechenbasis' },
  { id: 'takt', titel: 'Takt' },
  { id: 'vorgaben', titel: 'Vorgaben für den Assistenten' },
  { id: 'waechter', titel: 'Wächter' },
  { id: 'anliegen', titel: 'Anliegen an den Entwickler' },
  { id: 'selectline', titel: 'SelectLine-Abholdienst' },
  { id: 'gemerkte-entscheidungen', titel: 'Gemerkte Entscheidungen' },
  { id: 'journal', titel: 'Journal' },
]

/** Sprungmarken statt eigener Adressen: die Abschnitte stehen untereinander auf einer Seite. */
function Unterreiter({ abschnitte }: { abschnitte: { id: string; titel: string }[] }) {
  return (
    <nav className="unterreiter" aria-label="Abschnitte der Einstellungen">
      {abschnitte.map((a) => (
        <a
          key={a.id}
          href={`#${a.id}`}
          onClick={(e) => {
            e.preventDefault()
            document.getElementById(a.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }}
        >
          {a.titel}
        </a>
      ))}
    </nav>
  )
}
